import { CalendarIcon, ChevronDown, Loader2, UserCircle2, XIcon } from 'lucide-react';
import moment from 'moment';
import { useEffect, useId, useRef, useState } from 'react';
import { Chip, ChipRow, StickyActionBar } from '@/components/mobile';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { SmartDatetimeInput } from '@/components/ui/extension/smart-datetime-input';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import CustomerSearch from '@/components/utils/DragAndDropUploader/CustomerSearch';
import { MultiUserSelector } from '@/components/runner/utils/MultiUserSelector';
import { cn } from '@/lib/utils';
import type { RecurrenceRule, ReminderCustomer, ReminderPriority } from '@/slices/remindersApi';
import formatPhoneForUser from '@/utils/formatPhoneForUser';
import { usePhoneMask } from '@/hooks/usePhoneMask';
import { Group, Row } from '@/components/event-calendar/popups/EventPopup/components/PopupContent/components/JobEventForm';
import { NotificationSettingsField } from '@/components/notifications/NotificationSettingsField';
import {
  newReminderNotifications,
  readNotifications,
  singleOffset,
  type NotificationSource,
  type Notifications,
} from '@/components/notifications/notificationSettings';
import {
  MAX_ASSIGNEES,
  PRIORITY_CONFIG,
  PRIORITY_OPTIONS,
  RECURRENCE_OPTIONS,
  SNOOZE_PRESETS,
  getDefaultNotificationOffset,
  keepNotificationDefault,
} from './reminderHelpers';

interface BusinessUser {
	_id: string;
	name?: string;
	username?: string;
	active?: boolean;
}

export interface ReminderComposerValue {
  text: string;
  dateTime: string;
  notes: string;
  priority: ReminderPriority;
  recurrence: RecurrenceRule;
  notifications: Notifications;
  _assignees: string[];
  customer: ReminderCustomer | null;
}

export interface ReminderComposerSubmit {
  text: string;
  dateTime: string;
  notes: string;
  priority: ReminderPriority;
  recurrence: { rule: RecurrenceRule };
  notifications: Notifications;
  /** Empty means the creator on a new reminder */
  _assignees: string[];
  _customer: string | null;
}

interface ReminderComposerProps {
  initial?: Partial<Omit<ReminderComposerValue, 'notifications'>> & NotificationSource;
  /** Whoever created the reminder being edited; a new one has none yet */
  creatorId?: string;
  users?: BusinessUser[];
  showCustomer?: boolean;
  /** Sequence steps never repeat */
  hideRepeat?: boolean;
  saving?: boolean;
  submitLabel?: string;
  compact?: boolean;
  /**
   * Phone full-screen form: the fields scroll and Save sits in a bar pinned
   * above the keyboard. The parent supplies the Cancel button in its header.
   */
  stickyActions?: boolean;
  onSubmit: (value: ReminderComposerSubmit) => void;
  onCancel?: () => void;
  /**
   * The whole draft whenever it changes (and once when the form opens), so
   * a parent can bring it back as `initial` if the form has to mount again.
   */
  onChange?: (value: ReminderComposerValue) => void;
  /** The Notify panel was open when the form had to mount again */
  notifyOpen?: boolean;
  onNotifyOpenChange?: (open: boolean) => void;
}

// datetime-local wants local wall time without a zone (a machine format).
const toLocalInputValue = (value: string) => {
  if (!value) return '';
  const date = moment(value);
  return date.isValid() ? date.format('YYYY-MM-DDTHH:mm') : '';
};

const getCustomerContactInfo = (customer: ReminderCustomer | null): string => {
  if (!customer) return '';
  if (customer.phone) return formatPhoneForUser(customer.phone);
  if (customer.email) return customer.email;
  if (customer.facebookUserId) return 'Facebook User';
  if (customer.googleConversationId) return 'Google Message';
  return '';
};

const ReminderComposer = ({
  initial,
  creatorId,
  users = [],
  showCustomer = false,
  hideRepeat = false,
  saving = false,
  submitLabel = 'Save reminder',
  compact = false,
  stickyActions = false,
  onSubmit,
  onCancel,
  onChange,
  notifyOpen = false,
  onNotifyOpenChange,
}: ReminderComposerProps) => {
  const maskPhone = usePhoneMask();
  const notifyId = useId();
  // Phones get the native date and time wheels instead of the typed date field.
  const isMobile = useIsMobile();
  const [text, setText] = useState(initial?.text ?? '');
  const [dateTime, setDateTime] = useState(initial?.dateTime ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [priority, setPriority] = useState<ReminderPriority>(initial?.priority ?? 'none');
  const [recurrence, setRecurrence] = useState<RecurrenceRule>(initial?.recurrence ?? 'none');
  const [assignees, setAssignees] = useState<string[]>(initial?._assignees ?? []);
  const [customer, setCustomer] = useState<ReminderCustomer | null>(initial?.customer ?? null);
  // Mode-aware: editing (an initial reminder was passed) shows that reminder's
  // own settings — legacy docs without the field (undefined) mean "at time",
  // NOT the stored default. Creating starts from the device's sticky default.
  // Never `?? 0` here: null means "no notification" and must survive.
  const [initialNotifications] = useState<Notifications>(() =>
    initial
      ? readNotifications(initial, 'reminder')
      : newReminderNotifications(getDefaultNotificationOffset()),
  );
  const [notifications, setNotifications] = useState<Notifications>(initialNotifications);
  const hasInitialDetails = Boolean(
    initial?.customer ||
      (initial?.priority && initial.priority !== 'none') ||
      (initial?.recurrence && initial.recurrence !== 'none') ||
      initial?._assignees?.length ||
      initial?.notes,
  );
  const [showDetails, setShowDetails] = useState(hasInitialDetails);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    onChangeRef.current?.({
      text,
      dateTime,
      notes,
      priority,
      recurrence,
      notifications,
      _assignees: assignees,
      customer,
    });
  }, [text, dateTime, notes, priority, recurrence, notifications, assignees, customer]);

	const assignableUsers = users.filter((u) => u.active);

	const canSave = Boolean(text.trim()) && Boolean(dateTime) && !saving;

  const handleSubmit = () => {
    if (!canSave) return;
    // Only an actively-changed timing updates the device's sticky default —
    // submitting untouched (create default or a legacy reminder) is a no-op.
    keepNotificationDefault(notifications, singleOffset(initialNotifications.offsets));
    onSubmit({
      text: text.trim(),
      dateTime,
      notes: notes.trim(),
      priority,
      recurrence: { rule: recurrence },
      notifications,
      _assignees: assignees,
      _customer: customer?._id ?? null,
    });
  };

  return (
    <div className={cn('space-y-4', stickyActions && 'flex min-h-0 flex-1 flex-col space-y-0')}>
      <div
        className={cn(
          'space-y-4',
          stickyActions && 'min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-6 pt-4',
        )}
      >
      <div className="space-y-1.5">
        <Label htmlFor="reminder-text">Reminder</Label>
        <Input
          id="reminder-text"
          placeholder="What do you need to remember?"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={2000}
          disabled={saving}
          // Editing on a phone shouldn't throw the keyboard over the form.
          autoFocus={!(stickyActions && initial)}
          enterKeyHint={stickyActions ? 'done' : undefined}
          autoCapitalize="sentences"
          className="bg-background max-md:h-11"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSave) handleSubmit();
          }}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="reminder-when">When</Label>
        {isMobile ? (
          <>
            <Input
              id="reminder-when"
              type="datetime-local"
              value={toLocalInputValue(dateTime)}
              onChange={(e) =>
                setDateTime(e.target.value ? moment(e.target.value).toISOString() : '')
              }
              disabled={saving}
              className="h-11 bg-background [&::-webkit-date-and-time-value]:text-left"
            />
            <ChipRow aria-label="Quick times" className={cn('pb-0 pt-1.5', stickyActions ? '-mx-4' : 'px-0')}>
              {SNOOZE_PRESETS.map((preset) => (
                <Chip key={preset.label} onClick={() => setDateTime(preset.getDate())} disabled={saving}>
                  {preset.label}
                </Chip>
              ))}
            </ChipRow>
          </>
        ) : (
          <SmartDatetimeInput
            value={dateTime}
            onValueChange={setDateTime}
            placeholder="e.g. tomorrow at 3pm"
            buttonIcon={<CalendarIcon className="h-4 w-4" />}
            className="w-full bg-background"
            disabled={saving}
          />
        )}
      </div>

      <Group>
        <Row label="Notify" htmlFor={notifyId} wrap="field">
          <NotificationSettingsField
            id={notifyId}
            kind="reminder"
            value={notifications}
            onChange={setNotifications}
            assigneeIds={assignees}
            creatorId={creatorId}
            isNew={!creatorId}
            disabled={saving}
            reopen={notifyOpen}
            onOpenChange={onNotifyOpenChange}
          />
        </Row>
      </Group>

      {showDetails ? (
        <div className="space-y-4 border-t border-border pt-4">
          {showCustomer && (
            <div className="space-y-1.5">
              <Label>Customer</Label>
              {customer ? (
                <div className="flex items-center justify-between p-2.5 border border-border rounded-md bg-background text-sm">
                  <div className="flex items-center space-x-2 min-w-0">
                    <UserCircle2 className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium truncate">{maskPhone(customer.name)}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {maskPhone(getCustomerContactInfo(customer))}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setCustomer(null)}
                    className="h-7 w-7 text-muted-foreground hover:text-foreground max-md:size-11"
                    aria-label="Clear customer"
                    disabled={saving}
                  >
                    <XIcon className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <CustomerSearch
                  handleSelectCustomer={(c: ReminderCustomer) => setCustomer(c)}
                  disabled={saving}
                />
              )}
            </div>
          )}

          <div className={cn('grid gap-3', compact || hideRepeat ? 'grid-cols-1' : 'grid-cols-2')}>
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select
                value={priority}
                onValueChange={(v) => setPriority(v as ReminderPriority)}
                disabled={saving}
              >
                <SelectTrigger className="bg-background max-md:h-11 max-md:text-base">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((p) => (
                    <SelectItem key={p} value={p}>
                      <span className="flex items-center gap-2">
                        <span className={cn('h-2 w-2 rounded-full', PRIORITY_CONFIG[p].dot)} />
                        {p === 'none' ? 'No priority' : `${PRIORITY_CONFIG[p].label} priority`}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!hideRepeat && (
            <div className="space-y-1.5">
              <Label>Repeat</Label>
              <Select
                value={recurrence}
                onValueChange={(v) => setRecurrence(v as RecurrenceRule)}
                disabled={saving}
              >
                <SelectTrigger className="bg-background max-md:h-11 max-md:text-base">
                  <SelectValue placeholder="Repeat" />
                </SelectTrigger>
                <SelectContent>
                  {RECURRENCE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}
          </div>

					{assignableUsers.length > 0 && (
						<div className="space-y-1.5">
							<Label>Assign to</Label>
							<MultiUserSelector
								title="Unassigned"
								sheetTitle="Assign to"
								max={MAX_ASSIGNEES}
								_selectedUsers={assignees}
								onSelectUser={(user) =>
									setAssignees((current) =>
										current.includes(user._id)
											? current.filter((_user) => _user !== user._id)
											: [...current, user._id],
									)
								}
								disabled={saving}
								className="w-full bg-background font-normal max-md:min-h-11 max-md:text-base touch:min-h-11"
								placeholderClassName="text-sm text-muted-foreground max-md:text-base"
							/>
						</div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="reminder-notes">Notes</Label>
            <Textarea
              id="reminder-notes"
              placeholder="Add notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={5000}
              disabled={saving}
              className="bg-background min-h-[72px]"
            />
          </div>
        </div>
      ) : (
				<button
					type="button"
					className="flex w-full items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground max-md:min-h-11 max-md:text-[15px]"
					onClick={() => setShowDetails(true)}
				>
					<span>Add details</span>
					<span className="flex items-center gap-1.5 text-xs font-normal text-muted-foreground max-md:text-[13px]">
						Customer, priority, notes
						<ChevronDown className="h-4 w-4" />
					</span>
				</button>
      )}
      </div>

      {stickyActions ? (
        <StickyActionBar className="pb-[max(0.5rem,var(--safe-bottom))] kb-open:pb-2">
          <Button className="flex-1" onClick={handleSubmit} disabled={!canSave}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : submitLabel}
          </Button>
        </StickyActionBar>
      ) : (
      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        )}
        <Button size="sm" onClick={handleSubmit} disabled={!canSave}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : submitLabel}
        </Button>
      </div>
      )}
    </div>
  );
};

export default ReminderComposer;
